import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { AppExceptionModel } from '../../models/app-exception.model';
import { Video } from '../../models/video.model';
import * as videoActions from '../actions/video.actions';

export interface VideoState extends EntityState<Video> {
  loading: boolean;
  loaded: boolean;
  inProgress: boolean;
  error: AppExceptionModel | undefined;
}

export const videoAdapter: EntityAdapter<Video> = createEntityAdapter<Video>();

export const initialState: VideoState = videoAdapter.getInitialState({
  loading: false,
  loaded: false,
  inProgress: false,
  error: undefined,
});

export const videoReducer = createReducer(
  initialState,
  on(videoActions.getVideos, (state, {}) => ({
    ...state,
    loading: true,
    loaded: false,
    error: undefined,
  })),
  on(videoActions.getVideo, (state, {}) => ({
    ...state,
    inProgress: true,
    error: undefined,
  })),
  on(videoActions.loadVideo, (state, { videos }) => {
    if (videos.length > 0) {
      return videoAdapter.upsertMany(videos, {
        ...state,
        loading: false,
        loaded: true,
        inProgress: false,
        error: undefined,
      });
    } else {
      return {
        ...state,
        loading: false,
        loaded: true,
        inProgress: false,
        error: undefined,
      };
    }
  }),
  on(videoActions.getVideosFailure, (state, { error }) => ({
    ...state,
    loading: false,
    loaded: true,
    inProgress: false,
    error: error,
  })),
  on(videoActions.create, (state, { video }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(videoActions.createSuccess, (state, { video }) =>
    videoAdapter.addOne(video, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(videoActions.createFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(videoActions.update, (state, { video }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(videoActions.updateSuccess, (state, { video }) =>
    videoAdapter.upsertOne(video, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(videoActions.updateFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(videoActions.deleteAction, (state, { id }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(videoActions.deleteSuccess, (state, { id }) =>
    videoAdapter.removeOne(id, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(videoActions.deleteFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(videoActions.select, (state, { id }) => ({
    ...state,
    selectedVideoId: id,
    error: undefined,
    inProgress: false,
  })),
  on(videoActions.clear, (state) => initialState),
);
